import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useQuietRefresh } from '../hooks/useQuietRefresh';
import { isAccountLoggedOut, setAccountLoggedOut, subscribeAccountSession } from './accountSession';
import { fetchCrmAccess, rememberCrmAccess, rememberedCrmAccess, type CrmAccess, type CrmAccount } from './crmAccess';
import { DEFAULT_CRM_PAGE, crmFrameSrc, parseCrmMessage, postToCrm, type CrmTarget } from './crmBridge';
import { FrameContentPicker, type FramePickRequest } from './FrameContentPicker';
import { rendersInReact, type ReactPageGate } from './crmRail';
import { fanslyReactEnabled } from '../fansly/flag';
import { frameLocationToFollow, navigateMessage, parseAiAccountRoute, parseCrmRoute, type PendingFanChat } from './crmRoutes';

/**
 * The shell's two views of an account (ADR 0020): the AI view is this console,
 * the CRM view is the client CRM in a persistent iframe. On an account route
 * the mode is the URL (/account/:id = AI, /account/:id/crm/:page = CRM); on a
 * global page it is a sticky preference that only changes what the account
 * list links to and shows.
 */
export type ShellMode = 'ai' | 'crm';

const MODE_KEY = 'om_shell_mode';
const AI_ROUTES_KEY = 'om_last_ai_route';
const CRM_PAGES_KEY = 'om_last_crm_page';
/**
 * ADR 0024 stage 2: the React Chats page is on for everyone who can open an
 * OnlyFans Account in the CRM (the CRM still enforces which ones).
 */
const REACT_PAGE_GATE: ReactPageGate = { reactChats: true, fanslyReact: fanslyReactEnabled() };
/** Mount the hidden frame a moment after login so the first switch is instant. */
const PREWARM_DELAY_MS = 2500;

function readMap(key: string): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}
function writeMap(key: string, value: Record<string, string>) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}
function readMode(): ShellMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'crm' ? 'crm' : 'ai';
  } catch {
    return 'ai';
  }
}

export interface CrmHostValue {
  access: CrmAccess;
  crmAccounts: CrmAccount[];
  hasCrmAccess: (accountId: string) => boolean;
  /** Listed in the CRM but not on a plan: its CRM pages show the No-plan lock. */
  isPlanLocked: (accountId: string) => boolean;
  /** The console renders this account's page itself (React), not the frame. */
  rendersReact: (accountId: string, page: string) => boolean;
  /** Effective mode for the sidebar: the route's on account pages, else the preference. */
  mode: ShellMode;
  /** Switch views: navigates on account routes, flips the preference elsewhere. */
  switchMode: (mode: ShellMode) => void;
  /** Where an account row points in each mode. */
  crmHref: (accountId: string) => string;
  aiHref: (accountId: string) => string;
  /** Unread chats per account, as the CRM reports them (current account only for now). */
  unread: Record<string, number>;
  /** The CRM target of the current route, if any. */
  target: CrmTarget | null;
  frameRef: RefObject<HTMLIFrameElement | null>;
  frameMounted: boolean;
  frameSrc: string;
  /** The frame is the thing to show for this route. */
  frameVisible: boolean;
  /** The frame has shown the current target: the skeleton can go. */
  frameSettled: boolean;
  /** The CRM said this login may not open the current account. */
  frameDenied: boolean;
  /** Mount the frame now (e.g. on hover over the CRM switch). */
  ensureFrame: () => void;
  /** Go to Chats and open this fan's conversation. */
  openFanChat: (accountId: string, fanId: string) => void;
  /** The React Chats page reports its unread count (the frame does through crm:unread). */
  reportUnread: (accountId: string, count: number) => void;
  /** OnlyFans answered that this Account's session is logged out (gateway 401). */
  isLoggedOut: (accountId: string) => boolean;
  /** Forget a logged-out verdict (after a re-login). */
  clearLoggedOut: (accountId: string) => void;
  /** Reload the frame on the current target (its pages re-ask OnlyFans). */
  reloadFrame: () => void;
}

const CrmHostContext = createContext<CrmHostValue | undefined>(undefined);

export function CrmHostProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, logout } = useAuth();
  const gate = REACT_PAGE_GATE;
  const gateRef = useRef(gate);
  gateRef.current = gate;
  const location = useLocation();
  const navigate = useNavigate();

  const [access, setAccess] = useState<CrmAccess>({ state: 'loading' });
  const [storedMode, setStoredMode] = useState<ShellMode>(readMode);
  const [unread, setUnread] = useState<Record<string, number>>({});
  const [frameMounted, setFrameMounted] = useState(false);
  const [frameSrc, setFrameSrc] = useState('');
  const [frameReady, setFrameReady] = useState(false);
  const [frameLocation, setFrameLocation] = useState<{ accountId: string; page: string; message: string } | null>(null);
  const [deniedAccountId, setDeniedAccountId] = useState<string | null>(null);
  // Bumped whenever an Account's OnlyFans session verdict changes (accountSession.ts).
  const [sessionVersion, setSessionVersion] = useState(0);
  useEffect(() => subscribeAccountSession(() => setSessionVersion((n) => n + 1)), []);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const sentTarget = useRef<CrmTarget | null>(null);
  const aiRoutes = useRef<Record<string, string>>(readMap(AI_ROUTES_KEY));
  const crmPages = useRef<Record<string, string>>(readMap(CRM_PAGES_KEY));

  const target = useMemo(() => parseCrmRoute(location.pathname), [location.pathname]);
  const aiAccountId = useMemo(() => parseAiAccountRoute(location.pathname), [location.pathname]);
  const crmAccounts = useMemo(() => (access.state === 'ok' ? access.accounts : []), [access]);
  const crmIds = useMemo(() => new Set(crmAccounts.map((a) => a.account_id)), [crmAccounts]);
  const crmIdsRef = useRef(crmIds);
  crmIdsRef.current = crmIds;
  // A frame page asked for the console's content picker (Mass messages, the old Chats).
  const [framePick, setFramePick] = useState<FramePickRequest | null>(null);
  const hasCrmAccess = useCallback((accountId: string) => crmIds.has(accountId), [crmIds]);
  const lockedIds = useMemo(() => new Set(crmAccounts.filter((a) => a.plan_locked).map((a) => a.account_id)), [crmAccounts]);
  const isPlanLocked = useCallback((accountId: string) => lockedIds.has(accountId), [lockedIds]);
  // React pages are OnlyFans only: a Fansly account is always the frame (ADR 0024).
  const platforms = useMemo(() => new Map(crmAccounts.map((a) => [a.account_id, a.platform])), [crmAccounts]);
  const platformsRef = useRef(platforms);
  platformsRef.current = platforms;
  // Until the CRM has listed the accounts their platform is unknown: no React
  // page yet, or a Fansly account would briefly call the OnlyFans gateway.
  const rendersReact = useCallback(
    (accountId: string, page: string) => platforms.has(accountId) && rendersInReact(page, platforms.get(accountId), gate),
    [platforms, gate],
  );

  // Remember where each account was last seen in each view, and let the route
  // set the sticky mode so a return to a global page keeps the view in use.
  useEffect(() => {
    if (target?.accountId) {
      crmPages.current[target.accountId] = target.page;
      writeMap(CRM_PAGES_KEY, crmPages.current);
      if (storedMode !== 'crm') setStoredMode('crm');
    } else if (aiAccountId) {
      aiRoutes.current[aiAccountId] = `${location.pathname}${location.search}`;
      writeMap(AI_ROUTES_KEY, aiRoutes.current);
      if (storedMode !== 'ai') setStoredMode('ai');
    }
    // storedMode is intentionally not a dependency: this effect reacts to navigation only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, target, aiAccountId]);

  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, storedMode);
    } catch {
      /* ignore */
    }
  }, [storedMode]);

  // The CRM gate for this login. Reset on logout so a different login starts clean.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setAccess({ state: 'loading' });
      setFrameMounted(false);
      setFrameReady(false);
      setFrameLocation(null);
      setUnread({});
      sentTarget.current = null;
      return;
    }
    const token = api.getToken();
    if (!token) {
      setAccess({ state: 'none' });
      return;
    }
    const controller = new AbortController();
    // The last working gate paints at once; the CRM's answer replaces it.
    const userId = user.id;
    setAccess(rememberedCrmAccess(userId) ?? { state: 'loading' });
    fetchCrmAccess(token, controller.signal).then((result) => {
      if (controller.signal.aborted || result.state === 'loading') return;
      rememberCrmAccess(userId, result);
      setAccess((prev) => (JSON.stringify(prev) === JSON.stringify(result) ? prev : result));
    });
    return () => controller.abort();
  }, [isAuthenticated, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quiet re-check of the gate: CRM grants can change mid-session. The list in use
  // stays until the answer arrives, and an unreachable CRM keeps the last good one.
  const gateUserId = useRef(user?.id);
  gateUserId.current = user?.id;
  const refreshAccess = useCallback(async () => {
    const token = api.getToken();
    const forUser = gateUserId.current;
    if (!token) return;
    const result = await fetchCrmAccess(token);
    if (forUser !== gateUserId.current || (result.state !== 'ok' && result.state !== 'none')) return;
    if (forUser) rememberCrmAccess(forUser, result);
    setAccess((prev) => (JSON.stringify(prev) === JSON.stringify(result) ? prev : result));
  }, []);
  useQuietRefresh(refreshAccess, isAuthenticated && access.state !== 'loading');

  const mountFrame = useCallback(
    (initial: CrmTarget | null) => {
      setFrameMounted((mounted) => {
        if (mounted) return mounted;
        // A React page does not need the frame; warm it on the dashboard instead of
        // loading the CRM's own copy of that page (and its OnlyFans calls) unseen.
        const first =
          initial &&
          !rendersInReact(initial.page, initial.accountId ? platformsRef.current.get(initial.accountId) : null, gateRef.current)
            ? initial
            : { page: 'dashboard' as const };
        sentTarget.current = first;
        setFrameSrc(crmFrameSrc(first));
        return true;
      });
    },
    [],
  );

  // A locked account's pages would only answer no_plan: the frame never goes there.
  const targetAllowed =
    !!target && access.state === 'ok' && (!target.accountId || (crmIds.has(target.accountId) && !lockedIds.has(target.accountId)));

  // Mount for a CRM route as soon as the gate allows it; otherwise pre-warm on
  // the dashboard shortly after login so the first switch is instant.
  useEffect(() => {
    if (access.state !== 'ok' || frameMounted) return;
    if (targetAllowed) {
      mountFrame(target);
      return;
    }
    const timer = window.setTimeout(() => mountFrame(null), PREWARM_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [access.state, frameMounted, targetAllowed, target, mountFrame]);

  const ensureFrame = useCallback(() => {
    if (access.state === 'ok') mountFrame(target && targetAllowed ? target : null);
  }, [access.state, mountFrame, target, targetAllowed]);

  // A fan chat asked for by a React page (Collections' Message), sent with the next navigate.
  const pendingFan = useRef<PendingFanChat | null>(null);
  const [fanRequest, setFanRequest] = useState(0);
  const openFanChat = useCallback(
    (accountId: string, fanId: string) => {
      const chats = `/account/${encodeURIComponent(accountId)}/crm/chats`;
      // The React Chats page opens the fan from its own URL (ADR 0024).
      if (platformsRef.current.has(accountId) && rendersInReact('chats', platformsRef.current.get(accountId), gateRef.current)) {
        navigate(`${chats}?fan=${encodeURIComponent(fanId)}`);
        return;
      }
      pendingFan.current = { accountId, fanId };
      setFanRequest((n) => n + 1);
      navigate(chats);
    },
    [navigate],
  );

  // `?fan=` on a Chats route (Workspace's "Open in CRM") opens that fan's chat, once.
  const routeFan = useMemo(() => new URLSearchParams(location.search).get('fan')?.trim() || '', [location.search]);
  const sentRouteFan = useRef('');
  useEffect(() => {
    if (!routeFan || routeFan === sentRouteFan.current || target?.page !== 'chats') return;
    if (rendersInReact('chats', platforms.get(target.accountId), gate)) return;
    sentRouteFan.current = routeFan;
    pendingFan.current = { accountId: target.accountId, fanId: routeFan };
    setFanRequest((n) => n + 1);
  }, [routeFan, target, platforms, gate]);

  // While React Chats is on, the console plays the chime and notifies; the frame stays quiet.
  useEffect(() => {
    if (frameReady) postToCrm(frameRef.current, { type: 'crm:quiet', quiet: gate.reactChats === true });
  }, [frameReady, gate]);

  // Drive the frame to the route's target once it is listening. React pages
  // render beside the frame; it keeps its last page so going back is instant.
  useEffect(() => {
    if (!frameReady || !targetAllowed || !target) return;
    const message = navigateMessage(target, sentTarget.current, pendingFan.current, platforms.get(target.accountId ?? ''), gate);
    if (message && postToCrm(frameRef.current, message)) {
      sentTarget.current = target;
      if (message.type === 'crm:navigate' && message.fanId) pendingFan.current = null;
      setDeniedAccountId(null);
    }
  }, [frameReady, target, targetAllowed, fanRequest, platforms, gate]);

  // Messages from the frame.
  useEffect(() => {
    if (!frameMounted) return;
    const onMessage = (event: MessageEvent) => {
      const msg = parseCrmMessage(event, frameRef.current);
      if (!msg) return;
      switch (msg.type) {
        case 'crm:ready': {
          const token = api.getToken();
          if (token) postToCrm(frameRef.current, { type: 'crm:auth', accessToken: token });
          setFrameReady(true);
          break;
        }
        case 'crm:location': {
          setFrameLocation({ accountId: msg.accountId, page: msg.page, message: '' });
          // Navigation inside the frame (its own Sidebar) moves the console URL
          // with it, so reload and back/forward return to the page on screen.
          const current = parseCrmRoute(window.location.pathname);
          const followed = frameLocationToFollow(
            current,
            msg,
            crmIdsRef.current,
            platformsRef.current.get(current?.accountId ?? ''),
            gateRef.current,
          );
          if (followed?.accountId) {
            sentTarget.current = followed;
            navigate(`/account/${encodeURIComponent(followed.accountId)}/crm/${followed.page}`, { replace: true });
          }
          break;
        }
        case 'crm:workspace':
          // "pending" with a message is a settled state the CRM explains itself;
          // a bare "pending" is still loading.
          if (msg.mode === 'fansly') setFrameLocation({ accountId: msg.accountId, page: 'fansly', message: '' });
          else if (msg.mode === 'pending' && msg.message) setFrameLocation({ accountId: msg.accountId, page: '', message: msg.message });
          break;
        case 'crm:auth-expired':
          void api.refreshAccessToken().then((fresh) => {
            if (fresh) {
              postToCrm(frameRef.current, { type: 'crm:auth', accessToken: fresh });
            } else {
              void logout().finally(() => navigate('/login'));
            }
          });
          break;
        case 'crm:unread':
          // React Chats counts its own OnlyFans accounts (reportUnread).
          if (msg.accountId && gateRef.current.reactChats && platformsRef.current.get(msg.accountId) !== 'fansly') break;
          if (msg.accountId) setUnread((prev) => (prev[msg.accountId] === msg.count ? prev : { ...prev, [msg.accountId]: msg.count }));
          break;
        case 'crm:access-denied':
          setDeniedAccountId(msg.accountId);
          break;
        case 'crm:account-session':
          if (crmIdsRef.current.has(msg.accountId)) setAccountLoggedOut(msg.accountId, msg.loggedOut);
          break;
        case 'crm:no-access':
          setAccess({ state: 'none' });
          break;
        case 'crm:signed-out':
          break;
        case 'crm:pick-content':
          // Only for an account this login may open in the CRM.
          if (!crmIdsRef.current.has(msg.accountId)) break;
          setFramePick({ requestId: msg.requestId, accountId: msg.accountId, initial: msg.initial });
          postToCrm(frameRef.current, { type: 'crm:content-picker-opened', requestId: msg.requestId });
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameMounted, logout, navigate]);

  const mode: ShellMode = target?.accountId ? 'crm' : aiAccountId ? 'ai' : storedMode;

  const crmHref = useCallback(
    (accountId: string) => `/account/${encodeURIComponent(accountId)}/crm/${crmPages.current[accountId] ?? DEFAULT_CRM_PAGE}`,
    [],
  );
  const aiHref = useCallback(
    (accountId: string) => aiRoutes.current[accountId] ?? `/account/${encodeURIComponent(accountId)}`,
    [],
  );

  const switchMode = useCallback(
    (next: ShellMode) => {
      if (next === 'crm') {
        // From an AI account page the CRM opens that account's Messages.
        if (aiAccountId) navigate(`/account/${encodeURIComponent(aiAccountId)}/crm/${DEFAULT_CRM_PAGE}`);
        else setStoredMode('crm');
      } else if (target?.accountId) {
        // Back to the AI page that was open for this account before.
        navigate(aiHref(target.accountId));
      } else {
        setStoredMode('ai');
      }
    },
    [aiAccountId, aiHref, navigate, target],
  );

  const reportUnread = useCallback((accountId: string, count: number) => {
    setUnread((prev) => (prev[accountId] === count ? prev : { ...prev, [accountId]: count }));
  }, []);

  // sessionVersion re-creates these so consumers re-render when a verdict changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isLoggedOut = useCallback((accountId: string) => isAccountLoggedOut(accountId), [sessionVersion]);
  const clearLoggedOut = useCallback((accountId: string) => setAccountLoggedOut(accountId, false), []);
  const reloadFrame = useCallback(() => {
    if (!frameMounted) return;
    const first = target && targetAllowed && !rendersInReact(target.page, platformsRef.current.get(target.accountId ?? ''), gateRef.current)
      ? target
      : sentTarget.current ?? { page: 'dashboard' as const };
    sentTarget.current = first;
    setFrameReady(false);
    setFrameLocation(null);
    setFrameSrc(`${crmFrameSrc(first)}&r=${Date.now()}`);
  }, [frameMounted, target, targetAllowed]);

  const frameVisible =
    frameMounted && targetAllowed && !!target && !rendersInReact(target.page, platforms.get(target.accountId ?? ''), gate);
  const frameDenied = !!target?.accountId && deniedAccountId === target.accountId;
  const frameSettled =
    frameVisible &&
    !!frameLocation &&
    (target?.accountId
      ? frameLocation.accountId === target.accountId && (!!frameLocation.page || !!frameLocation.message)
      : frameLocation.page === 'dashboard');

  const value = useMemo<CrmHostValue>(
    () => ({
      access,
      crmAccounts,
      rendersReact,
      hasCrmAccess,
      isPlanLocked,
      mode,
      switchMode,
      crmHref,
      aiHref,
      unread,
      target,
      frameRef,
      frameMounted,
      frameSrc,
      frameVisible,
      frameSettled,
      frameDenied,
      ensureFrame,
      openFanChat,
      reportUnread,
      isLoggedOut,
      clearLoggedOut,
      reloadFrame,
    }),
    [access, crmAccounts, rendersReact, hasCrmAccess, isPlanLocked, mode, switchMode, crmHref, aiHref, unread, target, frameMounted, frameSrc, frameVisible, frameSettled, frameDenied, ensureFrame, openFanChat, reportUnread, isLoggedOut, clearLoggedOut, reloadFrame],
  );

  return (
    <CrmHostContext.Provider value={value}>
      {children}
      {framePick && <FrameContentPicker request={framePick} frameRef={frameRef} onEnd={() => setFramePick(null)} />}
    </CrmHostContext.Provider>
  );
}

export function useCrmHost() {
  const context = useContext(CrmHostContext);
  if (context === undefined) {
    throw new Error('useCrmHost must be used within a CrmHostProvider');
  }
  return context;
}

/** Composer and other OF pages that also render in unit tests without the host. */
export function useCrmHostOptional() {
  return useContext(CrmHostContext);
}
